/**
 * Modal - Reusable modal component with header, body, footer slots
 * 
 * Features:
 * - Customizable header, body, footer
 * - Close on overlay click
 * - Close on ESC key
 * - Focus trap
 * - Accessibility support
 */

import React, { useEffect, useRef } from 'react';

const Modal = ({
  isOpen = false,
  onClose,
  title,
  children,
  footer,
  size = 'medium', // 'small', 'medium', 'large', 'full'
  closeOnOverlay = true,
  closeOnEsc = true,
  showCloseButton = true,
  className = ''
}) => {
  const modalRef = useRef(null);
  const previousFocusRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      // Store previously focused element
      previousFocusRef.current = document.activeElement;
      
      // Focus modal
      modalRef.current?.focus();
      
      // Prevent body scroll
      document.body.style.overflow = 'hidden';
    } else {
      // Restore body scroll
      document.body.style.overflow = '';
      
      // Restore focus
      previousFocusRef.current?.focus();
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !closeOnEsc) return;

    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, closeOnEsc, onClose]);

  const handleOverlayClick = (e) => {
    if (closeOnOverlay && e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="admin-modal-overlay"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div 
        ref={modalRef}
        className={`admin-modal admin-modal--${size} ${className}`}
        tabIndex={-1}
      >
        {(title || showCloseButton) && (
          <div className="admin-modal__header">
            {title && (
              <h2 id="modal-title" className="admin-modal__title">
                {title}
              </h2>
            )}
            {showCloseButton && (
              <button
                className="admin-modal__close"
                onClick={onClose}
                aria-label="Close modal"
              >
                ×
              </button>
            )}
          </div>
        )}

        <div className="admin-modal__body">
          {children}
        </div>

        {footer && (
          <div className="admin-modal__footer">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

export const ConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  title = 'Confirm Action',
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmVariant = 'primary',
  loading = false
}) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="small"
      footer={
        <>
          <button
            className="admin-btn admin-btn--secondary"
            onClick={onClose}
            disabled={loading}
          >
            {cancelText}
          </button>
          <button
            className={`admin-btn admin-btn--${confirmVariant}`}
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? 'Processing...' : confirmText}
          </button>
        </>
      }
    >
      <p>{message}</p>
    </Modal>
  );
};

export const AlertModal = ({
  isOpen,
  onClose,
  title = 'Alert',
  message,
  variant = 'info', // 'info', 'success', 'warning', 'error'
  buttonText = 'OK'
}) => {
  const icons = {
    info: 'ℹ️',
    success: '✓',
    warning: '⚠️',
    error: '✕'
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="small"
      footer={
        <button
          className="admin-btn admin-btn--primary"
          onClick={onClose}
        >
          {buttonText}
        </button>
      }
    >
      <div className={`admin-alert-modal admin-alert-modal--${variant}`}>
        <span className="admin-alert-modal__icon">{icons[variant]}</span>
        <p className="admin-alert-modal__message">{message}</p>
      </div>
    </Modal>
  );
};

export default Modal;
